import {
  createRootRouteWithContext,
  Link,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Logo } from '@promanage/ui';
import { Icon } from '../components/Icon';
import {
  RAIL_ICON,
  RAIL_ITEM_ACTIVE_BG,
  RAIL_ITEM_ACTIVE_TEXT,
  RAIL_ITEM_IDLE_BG,
  RAIL_ITEM_IDLE_TEXT,
  RAIL_ITEM_INERT,
  railItem,
  railItemFill,
  railLabel,
} from '../components/railItem';
import { MobileNav } from '../components/MobileNav';
import { OfflineBar } from '../components/OfflineBar';
import { NAV, navAlsoActive, visibleNav, type NavItem } from '../components/nav';
import { UnreadMailBadge } from '../features/inbox/UnreadMail';
import { AccountMenu } from '../features/auth/AccountMenu';
import { NotificationsRailItem } from '../features/alerts/Notifications';
import { RailWorkspaces } from '../features/workspaces/WorkspaceNavigation';
import { UniversalSearch } from '../features/search/UniversalSearch';
import { ShortcutKeys } from '../features/shortcuts/ShortcutKeys';
import { keyShortcutFor } from '../features/shortcuts/goKeys';
import { SearchRailItem } from '../features/search/SearchTriggers';
import { Help } from '../features/help/Help';
import { HelpRailItem } from '../features/help/HelpTriggers';
import {
  MFA_PATH,
  authGate,
  isBarePath,
  isPublicPath,
  renderedPathname,
} from '../features/auth/gate';
import {
  orgListQueryOptions,
  resolveSession,
  useCurrentOrg,
  useSession,
} from '../features/auth/session';
import { useRailCollapsed } from '../lib/railCollapse';
import { useDocumentHeldAtTop } from '../lib/documentScroll';
import { RouteFailure } from '../components/RouteFailure';
import { UnsavedChangesGuard } from '../components/UnsavedChanges';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  /**
   * The guard.
   *
   * Runs before anything renders, so an unauthenticated visitor never sees a flash of the
   * application shell. It is a routing decision and nothing more — the server refuses every
   * request on its own evidence, and this file could be deleted without weakening a single
   * permission.
   */
  async beforeLoad({ context, location }) {
    if (isPublicPath(location.pathname)) return;

    const session = await resolveSession(context.queryClient);

    if (!session) {
      throw redirect({
        to: '/login',
        // Where they were trying to go, so the login lands them there rather than on the
        // dashboard. Read back through `safeRedirect`, which refuses anything off-site.
        search: { redirect: location.href },
      });
    }

    const gate = authGate(session);

    // An enrolled session can still need a fresh code for a trust step-up even when
    // `mfaVerifiedAt` is non-null. Let the page render the verifier; the server enforces whether
    // the code is required and rejects every protected request independently.
    if (location.pathname === MFA_PATH) return;

    if (gate !== 'ok') throw redirect({ to: MFA_PATH });

    /*
     * Warmed here, not awaited — and only once the rail is certain to be drawn.
     *
     * The rail names the organisation, and after a login the cache has just been cleared, so
     * without this the first thing a freshly signed-in operator sees is the brand tile holding
     * an em dash where their agency should be. Firing it here lets it travel alongside the
     * route's own loaders rather than starting after the shell has painted. Not awaited,
     * because nobody should wait on chrome, and `staleTime` makes it a no-op on the next
     * navigation.
     */
    void context.queryClient.prefetchQuery(orgListQueryOptions());
  },
  component: RootLayout,
  errorComponent: RouteFailure,
});

function RootLayout() {
  // Before the bare-path return: the login form is where a keyboard is opened first.
  useDocumentHeldAtTop();

  /*
   * The RENDERED path, not the pending one.
   *
   * `state.location` becomes the destination the instant a navigation starts, but the previous
   * route stays mounted in <Outlet/> until its replacement has finished loading. Choosing the
   * chrome from the location meant that for the whole of that window — after login, two round
   * trips: `auth.me`, then the dashboard's own loader — the shell was drawn around a login card
   * that had not gone anywhere yet. `renderedPathname` reads the match store the Outlet renders
   * from, so the two cannot disagree.
   */
  const pathname = useRouterState({
    select: (state) => renderedPathname(state.matches, state.location.pathname),
  });

  // Login, invite acceptance and the MFA prompt get the page to themselves. A rail full of
  // navigation you cannot use yet is noise, and a signed-out visitor should not be shown the
  // shape of an agency's console at all.
  if (isBarePath(pathname)) {
    return (
      // `scrollbar-gutter`, for the same reason <main> below carries it: the login card is
      // centred, so a scrollbar appearing would slide it sideways mid-page.
      <div className="flex h-full flex-col overflow-y-auto bg-paper text-ink [scrollbar-gutter:stable]">
        <Outlet />
      </div>
    );
  }

  return (
    // h-full, not h-dvh: html/body/#root are locked to 100% in styles.css, so this matches
    // exactly. h-dvh can exceed that as mobile browser chrome collapses, and with the
    // document set to overflow:hidden the excess would be clipped rather than scrollable.
    <div className="flex h-full flex-col bg-paper text-ink md:flex-row">
      <Rail />
      <MobileNav />

      {/*
       * Mounted by the shell, once, rather than by a page.
       *
       * It carries the application's only global key listener, so it has to outlive every route
       * change — Ctrl+K works on the dashboard, inside the reconcile queue and halfway through a
       * form, and a palette owned by a page would stop existing the moment somebody navigated.
       * It renders nothing until it is opened.
       */}
      <UniversalSearch />

      {/*
       * The application's own keys — `g` then a letter to any section, `?` for the list — and for
       * the same reason: they are the way between pages, so no page can own them.
       */}
      <ShortcutKeys />

      {/* Also the shell's, once: the leaving warning for every form on every page. */}
      <UnsavedChangesGuard />

      {/*
       * And the help panel, for the same reason as search: it stays open across navigation, so a
       * guide can be followed from one page to the next. Renders nothing until it is opened.
       */}
      <Help />

      {/*
       * One scroll container, the full width of everything beside the rail.
       *
       * A panel that sits beside a page — the dashboard's portfolio tree — is laid out BY
       * that page, under its own top bar. The shell used to render it here as a sibling of
       * <main>, which narrowed the scrollport and left one page in the product whose top bar
       * stopped short of the right edge while the panel rose past it.
       *
       * Padding lives INSIDE the scroll container, not on the wrapper around it. An
       * overflow:auto element clips its children at the padding box. With the padding
       * outside, the sticky glass top bar sat flush against that clip edge and lost its blur
       * and drop shadow along every side. Moving the padding in gives the shadow room to
       * render within the scrollport.
       *
       * `scrollbar-gutter: stable` — THE GUTTER IS RESERVED WHETHER OR NOT IT IS OCCUPIED.
       *
       * A classic scrollbar is taken out of this element's CONTENT box, so without the
       * reservation every page in the product was one width while it fitted and fifteen pixels
       * narrower once it did not — the whole console, top bar included, stepping left the moment
       * a list grew a row, and stepping back when a filter removed one. Reserving the space
       * makes the two cases identical, which is the only way the content can hold still.
       *
       * Costs nothing where scrollbars are overlays — macOS, iOS, Android all compute the gutter
       * to zero — so this is a Windows and Linux fix that the other platforms never notice.
       */}
      {/*
       * The bottom padding clears the floating mobile tab bar, which is `fixed` and therefore
       * reserves nothing for itself — `MobileNav` measures its own clearance and publishes it.
       * The token is 0px, so on desktop, where that bar is `display:none`, this is just `p-4`.
       */}
      <main className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 pb-[calc(1rem+var(--mobilenav-height))] [scrollbar-gutter:stable]">
        {/* Nothing at all while there is a connection — see OfflineBar. */}
        <OfflineBar />
        <Outlet />
      </main>
    </div>
  );
}

/**
 * The rail carries navigation AND app-level chrome — notifications and the account menu, which
 * holds the theme and Settings.
 *
 * Those are properties of the application, not of whatever page is open, so repeating them
 * above every page made them look like page controls. Here they are stated once.
 */
function Rail() {
  const roles = useSession()?.roles ?? [];
  const [collapsed] = useRailCollapsed();
  const org = useCurrentOrg();

  return (
    <nav
      aria-label="Main"
      /*
       * `hidden md:flex`: below `md` this is not the navigation — `MobileNav` is, as a bottom
       * bar. The rail used to try to be both, folding itself into a 64px strip that nine
       * full-width rows then fought over, and `display:none` here is what lets each be built
       * for the input it actually has. Only one is ever in the accessibility tree.
       */
      className={`hidden shrink-0 flex-col border-r border-rule bg-surface py-4 short:py-3 transition-[width] duration-[var(--duration-slow)] ease-[var(--ease-out)] md:flex ${
        collapsed ? 'w-[var(--rail-width)]' : 'w-[var(--rail-width-expanded)]'
      }`}
    >
      {/*
       * The brand slot. Collapsed, the tile is the ONLY thing on screen naming the product, so
       * it carries the accessible name itself. Expanded, "ProManage" is now visible text, and
       * the organisation sits directly beneath it — stated in plain words rather than implied
       * by a logo, for the same reason AccountMenu states it in the popover: someone from
       * ProManage working inside a customer's books must never be one glance away from
       * thinking they are in their own.
       *
       * The tile's inset swaps the same way `railItem` swaps an icon's: collapsed, `pl-[18px]`
       * centres the 40px tile in the 76px rail — `(76 − 40) / 2` — rather than leaving it at
       * the expanded inset and looking shoved left. `padding-left` transitions, so the tile
       * glides to centre in step with the rail's own width animation instead of snapping.
       */}
      <div
        className={`flex w-full shrink-0 items-center pr-3 transition-[padding-left] duration-[var(--duration)] ease-[var(--ease-out)] ${
          collapsed ? 'pl-[18px]' : 'pl-3'
        }`}
      >
        <span
          role={collapsed ? 'img' : undefined}
          aria-label={collapsed ? 'ProManage' : undefined}
          aria-hidden={collapsed ? undefined : true}
          className="flex size-10 shrink-0 items-center justify-center rounded-md bg-accent text-accent-ink"
        >
          <Logo size={28} />
        </span>
        <div
          className={`min-w-0 flex-1 overflow-hidden transition-[opacity,max-width,margin-left] duration-[var(--duration)] ease-[var(--ease-out)] ${
            collapsed ? 'ml-0 max-w-0 opacity-0' : 'ml-2.5 max-w-[10rem] opacity-100'
          }`}
        >
          <p className="truncate text-[1rem] font-extrabold tracking-[-0.01em]">ProManage</p>
          <p className="truncate text-[0.6875rem] text-ink-muted">{org?.name ?? '—'}</p>
        </div>
      </div>

      <div className="mt-6 min-h-0 flex-1 self-stretch overflow-y-auto px-3 short:mt-4">
        <div className="flex flex-col gap-1.5 short:gap-1">
          {/*
           * Above the sections, not below the hairline with theme and notifications. Search is the
           * fastest route to every place in the product, which makes it the head of the list of
           * places rather than a piece of chrome — see the note in SearchTriggers.tsx.
           */}
          <SearchRailItem />

          {visibleNav(NAV, roles).map((item) => (
            <RailLink key={item.to} item={item} collapsed={collapsed} />
          ))}
        </div>
        <RailWorkspaces collapsed={collapsed} />
      </div>

      {/*
       * A hairline divides navigation from chrome.
       *
       * Everything above it is a place in the product; everything below is about you or the
       * application — alerts and account. They share the rail's geometry, so without the rule
       * the only thing distinguishing "go to Reports" from "open your account" is that one of
       * them happens to be lower down.
       *
       * Two rows, not five. Theme, Settings and collapsing the rail are in the account menu, and the
       * bell is an icon beside the account button rather than a row of its own. Every row here is
       * one the list of places above cannot have, and on a 13" laptop five of them left room for
       * nine sections out of fourteen. Collapsed, 76px holds one control across, so the bell
       * stacks above the account instead — `flex-col-reverse` keeps the account at the bottom.
       *
       * Help is the second row, with a label. It sat beside the bell as a third square for a day,
       * and three controls across a 240px rail squeezed the account button to "Demo Mana…". As a
       * row it costs one row when expanded and nothing extra collapsed, where it was already a
       * stacked square.
       */}
      <div className="flex flex-col gap-1.5 self-stretch border-t border-rule px-3 pt-3 short:gap-1 short:pt-2">
        <HelpRailItem />

        <div
          className={`flex gap-1.5 short:gap-1 ${
            collapsed ? 'flex-col-reverse items-center' : 'flex-row items-center'
          }`}
        >
          <div className="w-full min-w-0 flex-1">
            <AccountMenu />
          </div>

          <NotificationsRailItem />
        </div>
      </div>
    </nav>
  );
}

function RailLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Unbuilt sections stay visible but inert — a rail that hides everything gives no sense of
  // the product's shape, and a link to a 404 is worse than a visibly disabled one.
  if (!item.ready) {
    return (
      <span
        aria-disabled="true"
        title={collapsed ? `${item.label} — not built yet` : undefined}
        className={`${railItem(collapsed)} ${RAIL_ITEM_INERT}`}
      >
        <Icon name={item.icon} size={RAIL_ICON} className="relative shrink-0" />
        <span className={railLabel(collapsed)}>{item.label} — not built yet</span>
      </span>
    );
  }

  const shape = railItem(collapsed);
  const alsoActive = navAlsoActive(item, pathname);

  return (
    <Link
      to={item.to}
      activeOptions={{ exact: item.to === '/' }}
      title={collapsed ? item.label : undefined}
      // The `g` chord that goes to the same place, announced where the place is.
      aria-keyshortcuts={keyShortcutFor(item.to)}
      className={`${shape} ${alsoActive ? RAIL_ITEM_ACTIVE_TEXT : RAIL_ITEM_IDLE_TEXT}`}
      activeProps={{ className: `${shape} ${RAIL_ITEM_ACTIVE_TEXT}` }}
      {...(alsoActive ? { 'aria-current': 'page' as const } : {})}
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={`${railItemFill(collapsed)} ${isActive || alsoActive ? RAIL_ITEM_ACTIVE_BG : RAIL_ITEM_IDLE_BG}`}
          />
          <Icon name={item.icon} size={RAIL_ICON} className="relative shrink-0" />
          <span className={railLabel(collapsed)}>{item.label}</span>
          {/*
           * At the row's end when the rail is open; on the icon's corner when it is collapsed
           * and the row is only as wide as the icon.
           */}
          {item.badge === 'unread-mail' ? (
            <UnreadMailBadge
              className={collapsed ? 'absolute top-1 left-7 short:top-0.5' : 'relative ml-1.5'}
            />
          ) : null}
        </>
      )}
    </Link>
  );
}
